import React, { useState, useRef, useCallback } from 'react';
import clsx from 'clsx';
import * as PopoverPrimitive from '@radix-ui/react-popover';

const variants = {
  default: 'bg-blue-200 dark:bg-blue-800',
  error: 'bg-red-200',
};

const arrowVariants = {
  default: 'fill-blue-200 dark:fill-blue-800',
  error: 'fill-red-200',
};

const btnSizes = {
  sm: '!text-base',
  md: '!text-xl',
};

export interface InfoPopoverProps {
  btnText?: string;
  btnIcon?: string;
  children: React.ReactNode;
  variant?: keyof typeof variants;
  size?: keyof typeof btnSizes;
}

export const InfoPopover = ({
  btnText,
  btnIcon,
  children,
  variant = 'default',
  size = 'md',
}: InfoPopoverProps) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOpen = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    timeoutRef.current = setTimeout(() => setOpen(false), 100);
  }, []);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        asChild
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleOpen}
        onBlur={handleClose}
      >
        <button type="button" className="cursor-default">
          <div className={`flex items-center ${btnText ? 'gap-2' : ''}`}>
            <span>{btnText}</span>{' '}
            {btnIcon && (
              <span className={clsx(btnSizes[size], 'pb-[2px] material-symbols-outlined')}>
                {btnIcon}
              </span>
            )}
          </div>
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align="center"
          sideOffset={8}
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className={clsx(
            variants[variant],
            'z-50 max-w-[310px] min-w-[300px] rounded-md shadow-lg p-2 text-sm break-words text-wrap',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
            'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2'
          )}
        >
          {children}
          <PopoverPrimitive.Arrow className={clsx(arrowVariants[variant])} width={20} height={8} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
